import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProduct, products } from '../data/products';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { Minus, Plus, ChevronLeft, Leaf, Truck, ShieldCheck, Check, Heart } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import Reviews from '../components/Reviews';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = getProduct(id);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [tab, setTab] = useState('description');
  const [variantId, setVariantId] = useState(product?.variants?.[0]?.id || null);
  const [giftRecipient, setGiftRecipient] = useState('');
  const [giftMyEmail, setGiftMyEmail] = useState(true);
  const { addItem } = useCart();
  const { isWishlisted, toggle } = useWishlist();
  const wished = product ? isWishlisted(product.id) : false;

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    setQty(1);
    setVariantId(product?.variants?.[0]?.id || null);
    setGiftRecipient('');
    setGiftMyEmail(true);
  }, [id, product]);

  if (!product) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif-elegant text-3xl mb-4">Product not found</h2>
        <Link to="/collections/all" className="btn-primary">Back to shop</Link>
      </div>
    );
  }

  const activeVariant = product.variants?.find(v => v.id === variantId) || null;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayCompare = activeVariant?.compareAtPrice || product.compareAtPrice;
  const onSale = displayCompare && displayCompare > displayPrice;
  const related = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);

  const handleAdd = () => {
    if (product.isGiftCard && !giftMyEmail && !giftRecipient.includes('@')) {
      toast.error('Please enter a valid recipient email.');
      return;
    }
    const extras = product.isGiftCard
      ? { deliverTo: giftMyEmail ? 'My email' : giftRecipient }
      : {};
    addItem(product, qty, activeVariant, extras);
    toast.success(`Added ${qty} \u00d7 ${product.title}${activeVariant ? ' \u2014 ' + activeVariant.label : ''}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-[#6f6a5f] hover:text-[#3a4a3b] mb-6">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-[#efe9db] overflow-hidden flex items-center justify-center p-4">
            <img src={product.images[activeImg]} alt={product.title} className="w-full h-full object-contain" />
          </div>
          {product.images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-3">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border-2 bg-[#efe9db] flex items-center justify-center p-1 transition-colors ${activeImg === i ? 'border-[#3a4a3b]' : 'border-transparent'}`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">{product.category}</div>
          <h1 className="font-serif-elegant text-4xl md:text-5xl mb-2">{product.title}</h1>
          {product.tagline && <div className="font-script text-xl text-[#7a8b6b] mb-4">{product.tagline}</div>}

          <div className="flex items-center gap-3 mb-6">
            {onSale ? (
              <>
                <span className="text-2xl text-[#7a3b2e] font-medium">${displayPrice.toFixed(2)}</span>
                <span className="text-lg text-[#6f6a5f] line-through">${displayCompare.toFixed(2)}</span>
                <span className="badge-sale">Sale</span>
              </>
            ) : (
              <span className="text-2xl font-medium">${displayPrice.toFixed(2)}</span>
            )}
          </div>

          <p className="text-[#4a4640] leading-relaxed mb-6">{product.description}</p>

          {product.benefits && product.benefits.length > 0 && (
            <ul className="space-y-1.5 mb-6 text-sm text-[#4a4640]">
              {product.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#7a8b6b] shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Variant selector */}
          {product.variants && (
            <div className="mb-6">
              <div className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-3">
                {product.isGiftCard ? 'Amount' : 'Options'}
              </div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map(v => (
                  <button
                    key={v.id}
                    onClick={() => setVariantId(v.id)}
                    className={`px-4 py-2 border text-sm transition-colors ${variantId === v.id ? 'border-[#3a4a3b] bg-[#3a4a3b] text-[#faf7f1]' : 'border-[#c9c1ad] text-[#4a4640] hover:border-[#3a4a3b]'}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Gift card recipient */}
          {product.isGiftCard && (
            <div className="mb-6 p-4 bg-[#efe9db]">
              <div className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-3">Send to</div>
              <div className="flex gap-3 mb-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={giftMyEmail} onChange={() => setGiftMyEmail(true)} /> My email
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="radio" checked={!giftMyEmail} onChange={() => setGiftMyEmail(false)} /> Recipient&apos;s email
                </label>
              </div>
              {!giftMyEmail && (
                <input
                  type="email"
                  value={giftRecipient}
                  onChange={e => setGiftRecipient(e.target.value)}
                  placeholder="recipient@email.com"
                  className="w-full bg-transparent border border-[#c9c1ad] px-3 py-2 text-sm focus:outline-none focus:border-[#3a4a3b]"
                />
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-6">
            <div className="flex items-center border border-[#c9c1ad]">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-[#efe9db]"><Minus className="w-4 h-4" /></button>
              <span className="px-5 text-sm">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-[#efe9db]"><Plus className="w-4 h-4" /></button>
            </div>
            <button onClick={handleAdd} className="btn-primary flex-1 min-w-[180px]">Add to Cart</button>
            <button
              onClick={() => toggle(product.id)}
              aria-label="Toggle wishlist"
              className={`w-12 h-12 flex items-center justify-center border transition-colors ${wished ? 'border-[#7a3b2e] text-[#7a3b2e] bg-[#7a3b2e]/5' : 'border-[#c9c1ad] text-[#2f2a24] hover:text-[#7a3b2e] hover:border-[#7a3b2e]'}`}
            >
              <Heart className={`w-5 h-5 ${wished ? 'fill-current' : ''}`} />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4 border-y border-[#e5decf] py-6 mb-8">
            <div className="flex items-start gap-2">
              <Leaf className="w-5 h-5 text-[#3a4a3b] shrink-0" />
              <div className="text-xs"><div className="font-medium">All-Natural</div><div className="text-[#6f6a5f]">Ethically sourced</div></div>
            </div>
            <div className="flex items-start gap-2">
              <Truck className="w-5 h-5 text-[#3a4a3b] shrink-0" />
              <div className="text-xs"><div className="font-medium">Fast Shipping</div><div className="text-[#6f6a5f]">3-7 business days</div></div>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-[#3a4a3b] shrink-0" />
              <div className="text-xs"><div className="font-medium">Small Batch</div><div className="text-[#6f6a5f]">Handmade</div></div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-[#efe9db] p-5 md:p-6">
            <div className="flex flex-wrap gap-x-5 gap-y-2 border-b border-[#c9c1ad]/60 pb-3 mb-4">
              {['description', 'ingredients', 'howToUse', product.size && 'size', product.note && 'note'].filter(Boolean).map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`text-xs sm:text-sm uppercase tracking-widest whitespace-nowrap pb-1 transition-colors ${tab === t ? 'text-[#3a4a3b] border-b-2 border-[#3a4a3b] font-medium' : 'text-[#6f6a5f] hover:text-[#3a4a3b]'}`}
                >
                  {t === 'howToUse' ? 'How to Use' : t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
            <div className="text-[#4a4640] leading-relaxed text-sm">
              {tab === 'description' && product.description}
              {tab === 'ingredients' && product.ingredients}
              {tab === 'howToUse' && product.howToUse}
              {tab === 'size' && product.size}
              {tab === 'note' && product.note}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <Reviews productId={product.id} />

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-24">
          <div className="text-center mb-10">
            <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">You may also love</div>
            <h2 className="font-serif-elegant text-3xl md:text-4xl">Related products</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
